import { gql, useQuery } from '@apollo/client';
import { css } from '@/styled-system/css';
import { Box, Heading, IconButton } from '@zillow/constellation-10';
import { IconChevronLeftFilled, IconUserOutline } from '@zillow/constellation-icons';
import { AvatarGroup, ConversationActions, ReportConversationModal } from '@zg-rentals/satellite-components';
import { useContext } from 'react';
import { ReportConversationReason, useConversationNav_GetConversationQuery } from '../../types/GraphqlTypes';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import {
  emitRenterProfileMobileIconClickEvent,
  RENTER_PROFILE_MOBILE_ICON_CLICK_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/renterProfile';
import { inboxPageDisplayNameVariable } from '../../lib/apollo';
import useConversationActions from '../../hooks/useConversationActions';
import useInboxRouterHelper from '../../hooks/useInboxRouterHelper';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import conversationUtils from '../../utils/conversationUtils';
import getActiveParticipantNames from '../../utils/getActiveParticipantNames';
import ConversationNavSkeleton from './skeleton';
import { FULLSTORY_EXCLUDE } from '../../constants/fullstory';
import useModal from '../../hooks/useModal';
import { ModalType } from '../../types/ModalType';
import { AppContext } from '../../contexts/AppContextProvider';
import { emitOpenReportProblemModalEvent } from '../../constants/clickstreamEvents/reportProblemModal';
import { TRIGGER_LOCATION } from '../../constants/clickstreamEvents/globals';
import { useAuth } from '@zg-rentals/auth-comet';

export const conversationNavWrapperStyles = css.raw({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  paddingBlock: 'default',
  borderBottom: '1px solid',
  borderBottomColor: 'gray.300',
  height: '80px',
  xlDown: {
    paddingLeft: 'tightest',
  },
  xxl: {
    paddingInline: 'default',
  },
});

const ConversationNav = (): JSX.Element | null => {
  const { isImpersonator } = useContext(AppContext);
  const { conversationId, listingAlias } = useContext(InboxPageContext);
  const { user } = useAuth();
  const { data: conversationNavData } = useConversationNav_GetConversationQuery({
    variables: {
      conversationId: conversationId!,
      listingAlias: listingAlias!,
    },
    fetchPolicy: 'cache-only',
  });
  const { data: inboxPageVariables } = useQuery(GET_INBOX_PAGE_VARIABLES, { fetchPolicy: 'cache-only' });
  const { inboxPageShowEmptyContainer } = inboxPageVariables;
  const { changeMobileView } = useInboxRouterHelper();
  const { activeModal, clearModal, setActiveModal } = useModal();
  const {
    archiveConversation,
    markConversationAsUnread,
    reportConversation,
    undoArchiveConversation,
    undoReportConversation,
  } = useConversationActions();

  if (!conversationNavData) {
    return <ConversationNavSkeleton />;
  }

  if (inboxPageShowEmptyContainer) {
    return null;
  }

  const { activeParticipantsInConversation, inquiry, isConversationBlockedByZillow, referenceName, status } =
    conversationNavData.ZRM_getConversationById;

  const { displayNames, fullParticipantList } = getActiveParticipantNames({
    activeParticipantsInConversation,
    referenceName: referenceName || '',
  });

  const displayName = inquiry?.isPhoneLead
    ? conversationUtils.getPhoneLeadParticipantDisplay({
        name: inquiry.name || '',
        phoneNumber: inquiry.phone || '',
        shouldFormatPhoneNumber: true,
        formattedPhoneNumberPrefix: 'Renter at ',
      })
    : displayNames;
  inboxPageDisplayNameVariable(displayName);

  const handleRenterProfileClick = () => {
    emitRenterProfileMobileIconClickEvent(user);
    changeMobileView(InboxPageMobileView.RenterProfile);
  };

  const handleBackArrowClick = () => {
    changeMobileView(InboxPageMobileView.ConversationList);
  };

  const handleUndoReportConversation = () => {
    return undoReportConversation({
      conversationIdToUpdate: conversationId!,
      reportReasonType: ReportConversationReason.NotReported,
    });
  };

  const handleMarkConversationAsUnread = () => {
    return markConversationAsUnread({
      conversationIdToUpdate: conversationId,
    });
  };

  const handleOpenReportConversationModal = () => {
    emitOpenReportProblemModalEvent(user, TRIGGER_LOCATION.IreneInbox);
    setActiveModal(ModalType.ReportConversationModal);
  };

  const viewRenterProfileButton = (
    <IconButton
      data-eventid={RENTER_PROFILE_MOBILE_ICON_CLICK_EVENT_TYPE_ID}
      onClick={handleRenterProfileClick}
      emphasis="bare"
      icon={<IconUserOutline />}
      shape="square"
      size="md"
      title="View renter profile"
      tone="brand"
      css={{
        xxl: {
          display: 'none',
        },
      }}
    />
  );

  return (
    <>
      {activeModal === ModalType.ReportConversationModal && (
        <ReportConversationModal
          isImpersonator={isImpersonator}
          name={displayName}
          onCancelClick={clearModal}
          onReportClick={({ reason, comments }) =>
            reportConversation({
              conversationIdToUpdate: conversationId!,
              reportReasonType: reason,
              userReportComment: comments,
            })
          }
        />
      )}

      <Box
        data-testid="conversation-nav"
        aria-label={`Chat window for conversation with ${displayName}`}
        css={conversationNavWrapperStyles}
      >
        <IconButton
          emphasis="bare"
          icon={<IconChevronLeftFilled onClick={handleBackArrowClick} />}
          shape="square"
          size="md"
          title="Go back to conversation list"
          tone="brand"
          css={{
            marginRight: 'default',
            xxl: {
              display: 'none',
            },
          }}
        />

        <AvatarGroup participants={fullParticipantList} variant="default" />

        <Heading
          level={2}
          textStyle="body-lg-bold"
          css={{
            truncate: true,
            width: '100%',
            maxWidth: '100%',
            paddingLeft: 'default',
            textTransform: 'capitalize',
          }}
          className={FULLSTORY_EXCLUDE}
        >
          {displayName}
        </Heading>

        <ConversationActions
          actions={{
            archiveConversation,
            markConversationAsUnread: handleMarkConversationAsUnread,
            openReportConversationModal: handleOpenReportConversationModal,
            undoArchiveConversation,
            undoReportConversation: handleUndoReportConversation,
          }}
          additionalActionButton={viewRenterProfileButton}
          breakpoint="xxl"
          isBlocked={isConversationBlockedByZillow}
          isImpersonator={isImpersonator}
          shouldShowMarkUnread={true}
          status={status}
        />
      </Box>
    </>
  );
};

export default ConversationNav;

const GET_INBOX_PAGE_VARIABLES = gql`
  query {
    inboxPageShowEmptyContainer @client
  }
`;
